import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
    RightOutlined,
    CarOutlined,
    SaveOutlined,
    ArrowLeftOutlined,
    TagOutlined,
    CalendarOutlined,
    EnvironmentOutlined
} from '@ant-design/icons';
import { vehiculesServices } from '../../common/services';
import citiesService from '../../common/services/cities.service';
import {
    Spin, Typography, Card, Breadcrumb, Form, Input, InputNumber,
    Select, Button, message, Row, Col, Grid, Tag
} from 'antd';

const { Title } = Typography;
const { Option } = Select;

// Couleurs pour les catégories
const CATEGORY_COLORS = {
    'econom': '#52c41a',
    'comfort': '#1890ff',
    'comfort_plus': '#722ed1',
    'business': '#fa8c16',
    'start': '#13c2c2',
    'standart': '#2f54eb',
    'moto': '#eb2f96',
    'intercity': '#faad14',
    'courier': '#f5222d',
    'express': '#fa541c',
    'pool': '#a0d911',
    'minivan': '#597ef7',
};

// Liste des catégories disponibles
const AVAILABLE_CATEGORIES = [
    'econom', 'comfort', 'comfort_plus', 'business', 'start', 'standart',
    'moto', 'intercity', 'courier', 'express', 'pool', 'minivan'
];

// Liste des status disponibles
const AVAILABLE_STATUS = [
    { value: 'working', label: 'En service' },
    { value: 'not_working', label: 'Hors service' },
    { value: 'repairing', label: 'En réparation' },
    { value: 'no_driver', label: 'Sans chauffeur' },
    { value: 'pending', label: 'En attente' },
];

// Liste des couleurs courantes
const AVAILABLE_COLORS = [
    'Noir', 'Blanc', 'Gris', 'Argent', 'Rouge', 'Bleu', 'Vert',
    'Jaune', 'Orange', 'Marron', 'Beige', 'Or', 'Bronze'
];

const ModifierVehicule = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();

    const [vehicule, setVehicule] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [cities, setCities] = useState([]);

    // Breakpoints & tailles adaptatives
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;
    const controlSize = isMobile ? 'small' : 'middle';

    // Chargement des villes
    useEffect(() => {
        citiesService.getAll()
            .then((res) => {
                const allCities = Array.isArray(res) ? res : (res.data || []);
                const activeCities = allCities.filter(c => c.isActive === true);
                setCities(activeCities);
            })
            .catch((err) => console.error('Erreur chargement villes:', err));
    }, []);

    // Chargement initial
    useEffect(() => {
        if (!id) return;
        setLoading(true);
        vehiculesServices.getById(id)
            .then((data) => {
                setVehicule(data);
                // Remplir le formulaire avec les données
                form.setFieldsValue({
                    marque: data.marque,
                    modele: data.modele,
                    couleur: data.couleur,
                    immatriculation: data.immatriculation,
                    annee: data.annee,
                    status: data.status,
                    kilometrage: data.kilometrage || 0,
                    categories: data.categories || [],
                    city: data.city?._id || data.city || undefined,
                });
            })
            .catch((err) => {
                console.error('Erreur chargement véhicule :', err);
                message.error('Erreur lors du chargement du véhicule');
            })
            .finally(() => setLoading(false));
    }, [id, form]);

    const onFinish = async (values) => {
        setSaving(true);
        try {
            await vehiculesServices.update(id, values);
            message.success('Véhicule mis à jour avec succès');
            navigate(`/dashboard/vehicule/${id}`);
        } catch (error) {
            console.error('Erreur mise à jour véhicule :', error);
            if (error.response?.data?.message) {
                message.error(error.response.data.message);
            } else {
                message.error('Erreur lors de la mise à jour du véhicule');
            }
        } finally {
            setSaving(false);
        }
    };

    // Tag render pour les catégories
    const tagRender = (props) => {
        const { label, value, closable, onClose } = props;
        const color = CATEGORY_COLORS[value] || '#8c8c8c';
        return (
            <Tag
                color={color}
                closable={closable}
                onClose={onClose}
                style={{ marginRight: 3, color: 'white' }}
            >
                {label.toUpperCase()}
            </Tag>
        );
    };

    if (loading) {
        return (
            <div style={{ padding: 40, textAlign: 'center', minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spin tip="Chargement du véhicule..." size="large" />
            </div>
        );
    }

    if (!vehicule) {
        return (
            <div style={{ padding: 40, textAlign: 'center' }}>
                <Title level={4}>Véhicule non trouvé</Title>
                <Button type="primary" onClick={() => navigate('/dashboard/vehicules')}>
                    Retour à la liste
                </Button>
            </div>
        );
    }

    return (
        <div className="content-wrapper">
            <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: 24, paddingRight: 24 }}>
                {/* Breadcrumb */}
                <Breadcrumb
                    separator={<RightOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />}
                    style={{ marginBottom: 12, marginTop: 0, paddingTop: 0 }}
                >
                    <Breadcrumb.Item>
                        <Link to="/dashboard" style={{ color: '#1890ff' }}>Accueil</Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        <Link to="/dashboard/vehicules" style={{ color: '#1890ff' }}>Véhicules</Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        <Link to={`/dashboard/vehicule/${id}`} style={{ color: '#1890ff' }}>Détails</Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        <span style={{ color: '#595959' }}>Modifier</span>
                    </Breadcrumb.Item>
                </Breadcrumb>

                {/* Header avec actions */}
                <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
                    <Col>
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate(`/dashboard/vehicule/${id}`)}
                        >
                            Retour
                        </Button>
                    </Col>
                    <Col>
                        <Title level={4} style={{ margin: 0 }}>
                            <CarOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                            Modifier : {vehicule.marque} {vehicule.modele}
                        </Title>
                    </Col>
                </Row>

                {/* Formulaire */}
                <Card
                    style={{ borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                    bodyStyle={{ padding: isMobile ? 16 : 32 }}
                >
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onFinish}
                        size={controlSize}
                    >
                        <Row gutter={[24, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="marque"
                                    label={<><CarOutlined style={{ marginRight: 8 }} />Marque</>}
                                    rules={[{ required: true, message: 'La marque est requise' }]}
                                >
                                    <Input placeholder="Ex: Toyota" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="modele"
                                    label={<><CarOutlined style={{ marginRight: 8 }} />Modèle</>}
                                >
                                    <Input placeholder="Ex: Corolla" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={[24, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="immatriculation"
                                    label={<><TagOutlined style={{ marginRight: 8 }} />Immatriculation</>}
                                    rules={[{ required: true, message: "L'immatriculation est requise" }]}
                                >
                                    <Input placeholder="Ex: AB-1234-CD" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="annee"
                                    label={<><CalendarOutlined style={{ marginRight: 8 }} />Année</>}
                                    rules={[{ required: true, message: "L'année est requise" }]}
                                >
                                    <InputNumber
                                        min={1990}
                                        max={new Date().getFullYear() + 1}
                                        style={{ width: '100%' }}
                                        placeholder="Ex: 2022"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={[24, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="couleur"
                                    label={<><span style={{ marginRight: 8 }}>🎨</span>Couleur</>}
                                    rules={[{ required: true, message: 'La couleur est requise' }]}
                                >
                                    <Select
                                        showSearch
                                        placeholder="Sélectionner une couleur"
                                        optionFilterProp="children"
                                        allowClear
                                    >
                                        {AVAILABLE_COLORS.map(color => (
                                            <Option key={color} value={color}>{color}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="status"
                                    label="Status"
                                >
                                    <Select placeholder="Sélectionner un status">
                                        {AVAILABLE_STATUS.map(st => (
                                            <Option key={st.value} value={st.value}>{st.label}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={[24, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="city"
                                    label={<><EnvironmentOutlined style={{ marginRight: 8, color: '#1890ff' }} />Ville</>}
                                >
                                    <Select
                                        showSearch
                                        placeholder="Sélectionner une ville"
                                        optionFilterProp="children"
                                        allowClear
                                    >
                                        {cities.map(city => (
                                            <Option key={city._id} value={city._id}>{city.name}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="kilometrage"
                                    label="Kilométrage"
                                >
                                    <InputNumber
                                        min={0}
                                        style={{ width: '100%' }}
                                        placeholder="Ex: 50000"
                                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                        parser={value => value.replace(/\s/g, '')}
                                        addonAfter="km"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={[24, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="categories"
                                    label="Classes de service"
                                >
                                    <Select
                                        mode="multiple"
                                        placeholder="Sélectionner les classes de service"
                                        tagRender={tagRender}
                                        style={{ width: '100%' }}
                                    >
                                        {AVAILABLE_CATEGORIES.map(cat => (
                                            <Option key={cat} value={cat}>
                                                {cat.toUpperCase()}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
                            <Row justify="end" gutter={16}>
                                <Col>
                                    <Button
                                        onClick={() => navigate(`/dashboard/vehicule/${id}`)}
                                    >
                                        Annuler
                                    </Button>
                                </Col>
                                <Col>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={saving}
                                        icon={<SaveOutlined />}
                                        style={{
                                            borderRadius: 8,
                                            background: 'linear-gradient(135deg, #52c41a 0%, #95de64 100%)',
                                            border: 'none'
                                        }}
                                    >
                                        Enregistrer les modifications
                                    </Button>
                                </Col>
                            </Row>
                        </Form.Item>
                    </Form>
                </Card>
            </div>
        </div>
    );
};

export default ModifierVehicule;
